import React, { useState, useEffect, useMemo } from 'react';
import get from 'lodash/get';
import { useDispatch, useSelector } from 'react-redux';

import { isEqual } from 'lodash';
import { dashboardsSelector } from '@store/tempDashboard/selectors';
import * as tempDashboardActions from '@store/tempDashboard/actions';
import { updateDashboardById } from '@store/dashboards/update/actions';
import { DASHBOARD_TYPE, PAGE_TYPE, SAVE_SERVICE_DASHBOARD_KEYS } from '@utils/constants';
import usePageTitle from '@src/hooks/usePageTitle';
import useKPI from '@src/hooks/useKPI';
import Threshold from './Threshold';
import Noc from './Noc';
import { savingSelector } from '@store/noc/dashboard/selectors';
import useSaveSet, { SavingStateObjectType } from '@components/saving/useSaveSet';
import { Provider, getDefaultStore } from 'jotai';
import usePageMeta from '@src/hooks/usePageMeta';
import { isSharingAtom } from '@src/atoms/pageMeta';
import { useParams } from 'react-router-dom';

// Use the same default store the saga uses so currentKpiId etc. are in one place (no ref needed).
const defaultStore = getDefaultStore();

const NocDashboard = (props) => {
  const { pageType, nocStore, tabs, store = defaultStore } = props;
  const [view, setView] = useState('noc');
  const { id: currentShareId = SAVE_SERVICE_DASHBOARD_KEYS.NOC } = useParams();

  const dashboards = useSelector(dashboardsSelector);
  const tempDashboardId = `saved-${currentShareId}`;
  const tempDashboard = dashboards[tempDashboardId];
  const dispatch = useDispatch();
  const lastLocation = null;

  // TODO: save store actions should happen in a component
  // to avoid parent re-renders

  const savingState = useSelector(savingSelector);

  // workaround until new share hoc is used
  useMemo(() => {
    if (store) {
      store.set(isSharingAtom, pageType === PAGE_TYPE.SHARING);
    }
  }, [pageType]);

  const { pageKey } = usePageMeta({ id: currentShareId, store });

  const saveData: SavingStateObjectType = useMemo(() => {
    return {
      meta: {
        dashboardTypeId: DASHBOARD_TYPE.NOC,
      },
      config: savingState,
    };
  }, [savingState]);

  useSaveSet({
    pageKey,
    saveData,
  });

  usePageTitle({ text: 'Real Time' });
  useKPI();

  useEffect(() => {
    const prevPath = lastLocation ? lastLocation.pathname : null;
    const resp = get(nocStore, 'dashboard.response');

    if (resp && resp.isSaved) {
      const key = `saved-${resp.id}`;
      if (dashboards[key] == null) {
        dispatch(
          tempDashboardActions.createSavedDashboard(
            resp.id,
            resp.name,
            DASHBOARD_TYPE.NOC,
            nocStore,
          ),
        );
      }
    }

    if (
      pageType === PAGE_TYPE.SHARING &&
      tabs.tabsData[currentShareId] &&
      !isEqual(nocStore, tabs.tabsData[currentShareId])
    ) {
      tabs.tabsData[currentShareId].config = nocStore;
      tabs.tabsData[currentShareId].unsaved = true;
      tabs.tabsData[currentShareId].isSaved = false;
    }

    // TODO: why is this check basedon last location required?
    if (pageType === PAGE_TYPE.SAVED_DASHBOARD && prevPath?.includes('noc/sharing')) {
      const { dashboard } = tempDashboard;
      setTimeout(() => {
        dispatch(updateDashboardById(currentShareId, tempDashboard.title, dashboard, false));
      }, 2000);
    }
  }, [nocStore]);

  return (
    <Provider store={store}>
      <React.Fragment>
        {view === 'noc' ? (
          <Noc
            pageType={pageType}
            currentShareId={currentShareId}
            setView={setView}
            pageKeyForSaveService={pageKey}
            {...props}
          />
        ) : (
          <Threshold setView={setView} {...props} />
        )}
      </React.Fragment>
    </Provider>
  );
};

export default NocDashboard;
